import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThumbsDown, ThumbsUp } from "lucide-react";

import { Area, Botao } from "@/components/sistema";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

/**
 * "Gostou do novo desenho?" — polegar para cima ou para baixo.
 *
 * Substitui o botão que deixava a pessoa voltar ao desenho anterior. A decisão
 * do dono: em vez de manter dois apps vivos para comparar, fica um só e a
 * pessoa diz o que achou. O que a métrica perde em rigor, o código ganha em
 * não ter que existir duas vezes.
 *
 * Grava na tabela `feature_ratings`, que já existia e já é lida no painel de
 * admin — nenhuma tabela nova, nenhum SQL para rodar. O polegar vira nota:
 * 5 para cima, 1 para baixo, que é a escala que a tabela já usa. O comentário
 * é opcional e só aparece depois do toque, porque pedir texto antes da opinião
 * é o jeito mais rápido de não receber nem uma coisa nem outra.
 */

/** Uma versão por relançamento: assim dá para comparar rodadas. */
export const RECURSO_DESIGN = "design-2026-09";

const LS_JA_AVALIOU = `fr_rated_${RECURSO_DESIGN}`;

export function jaAvaliouDesign(): boolean {
  try {
    return localStorage.getItem(LS_JA_AVALIOU) === "1";
  } catch {
    return false;
  }
}

export function AvaliacaoDesign({ onPronto }: { onPronto?: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [voto, setVoto] = useState<"gostou" | "nao" | null>(null);
  const [comentario, setComentario] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (jaAvaliouDesign()) setEnviado(true);
  }, []);

  const gravar = async (escolha: "gostou" | "nao", texto: string) => {
    if (!user) return;
    try {
      await supabase.from("feature_ratings" as any).insert({
        user_id: user.id,
        feature: RECURSO_DESIGN,
        rating: escolha === "gostou" ? 5 : 1,
        would_use_again: escolha === "gostou",
        would_recommend: escolha === "gostou",
        expected_result: texto.trim() || null,
      });
      try { localStorage.setItem(LS_JA_AVALIOU, "1"); } catch { /* sem armazenamento */ }
    } catch (err) {
      // O voto não vale um erro na cara da pessoa: ela já disse o que achou.
      console.error("[AvaliacaoDesign] não gravou:", err);
    }
  };

  const votar = async (escolha: "gostou" | "nao") => {
    setVoto(escolha);
    setSalvando(true);
    await gravar(escolha, "");
    setSalvando(false);
  };

  const enviarComentario = async () => {
    if (!voto) return;
    setSalvando(true);
    // Segunda linha com o texto: a primeira já registrou o voto, e perder o
    // voto porque a pessoa desistiu de escrever seria o pior dos mundos.
    await gravar(voto, comentario);
    setSalvando(false);
    setEnviado(true);
    onPronto?.();
  };

  if (enviado) {
    return (
      <p
        className="leading-snug"
        style={{ padding: "0 var(--gutter)", fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)" }}
      >
        {t("design_rating.thanks", "Gracias. Lo tendremos en cuenta.")}
      </p>
    );
  }

  return (
    <div style={{ paddingLeft: "var(--gutter)", paddingRight: "var(--gutter)" }}>
      <div className="cartao px-5 py-6">
        <p className="font-bold leading-tight" style={{ fontSize: "var(--t-linha)" }}>
          {t("design_rating.question", "¿Te gusta el nuevo diseño?")}
        </p>

        <div className="mt-5 grid gap-3">
          {([
            { valor: "gostou" as const, icone: ThumbsUp, rotulo: t("design_rating.yes", "Sí, me gusta") },
            { valor: "nao" as const, icone: ThumbsDown, rotulo: t("design_rating.no", "No me gusta") },
          ]).map((o) => {
            const escolhido = voto === o.valor;
            return (
              <button
                key={o.valor}
                type="button"
                aria-pressed={escolhido}
                disabled={salvando}
                onClick={() => votar(o.valor)}
                className="flex w-full items-center gap-4 rounded-xl border-2 px-5 text-left"
                style={{
                  minHeight: "var(--alvo)",
                  borderColor: escolhido ? "hsl(var(--foreground))" : "hsl(var(--border))",
                  background: escolhido ? "hsl(var(--secondary))" : "transparent",
                }}
              >
                <o.icone className="h-7 w-7 shrink-0" strokeWidth={escolhido ? 2.6 : 2} />
                <span
                  className={escolhido ? "font-bold" : "font-semibold"}
                  style={{ fontSize: "var(--t-linha)" }}
                >
                  {o.rotulo}
                </span>
              </button>
            );
          })}
        </div>

        {voto && (
          <div className="mt-5" style={{ marginLeft: "calc(var(--gutter) * -1)", marginRight: "calc(var(--gutter) * -1)" }}>
            <Area
              rotulo={t("design_rating.why", "¿Quieres contarnos por qué? (opcional)")}
              valor={comentario}
              onMudar={setComentario}
              linhas={3}
              maximo={500}
              desabilitado={salvando}
            />
            <div style={{ padding: "16px var(--gutter) 0" }}>
              <Botao
                rotulo={salvando ? t("common.saving", "Guardando…") : t("design_rating.send", "Enviar")}
                desabilitado={salvando}
                onClick={enviarComentario}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
